import { StatusBadge, type ReservationStatus } from '@/components/ui/status-badge';
import { Badge } from '@/components/ui/badge';

// One place that turns a domain booking status into a badge.
//
// The domain speaks snake_case (`checked_out`, `no_show`); the design-kit badge
// speaks hyphens (`checked-out`, `no-show`) and indexes a fixed record by that
// value. A status the record does not know yields `undefined`, and the badge
// then reads `.variant` off it — a crash, not a fallback.
//
// That is exactly what happened on the cabinet's booking detail (OH-160): it
// cast `booking.status as ReservationStatus` and blew up the whole page for any
// stay that had been checked in, checked out or marked no-show. Three of the
// six statuses happen to spell the same in both vocabularies, which is why it
// looked fine for confirmed/pending/cancelled bookings.
//
// Two routes already carried private copies of this mapping. A third place
// reaching for a cast rather than the mapping is what a duplicated helper
// invites, so the copies are gone and this is the only one.
const STATUS_MAP: Record<string, ReservationStatus> = {
  confirmed: 'confirmed',
  pending: 'pending',
  pending_payment: 'pending',
  cancelled: 'cancelled',
  canceled: 'cancelled',
  checked_in: 'checked-in',
  checked_out: 'checked-out',
  no_show: 'no-show',
};

/**
 * StatusChip renders a booking status, humanising anything it does not know
 * rather than failing. A status added to the backend later shows up as a plain
 * neutral chip until it is mapped here — never as a blank page.
 */
export function StatusChip({ status }: { status: string }) {
  const mapped = STATUS_MAP[status.toLowerCase()];
  if (mapped) return <StatusBadge status={mapped} />;
  const label = status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  return (
    <Badge variant="neutral" dot>
      {label}
    </Badge>
  );
}
